import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import OptimizedImage from '@/components/OptimizedImage';
import { API } from '@/App';
import axios from 'axios';
import { Calendar, User, ArrowLeft, Tag } from 'lucide-react';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-white/60">Yükleniyor...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <Navbar />
        <div className="container mx-auto px-4 pt-32">
          <div className="text-center text-white/60" data-testid="post-not-found">Yazı bulunamadı.</div>
          <div className="text-center mt-4">
            <Link to="/blog" className="text-[#FF6B35] hover:text-[#F7931E]">
              Blog'a Dön
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Helmet>
        <title>{post.title} - Ener Yazılım Blog</title>
        <meta name="description" content={post.excerpt || post.title} />
        <meta name="keywords" content={post.tags ? post.tags.join(', ') : 'blog, teknoloji, yazılım'} />
        <link rel="canonical" href={`https://www.eneryazilim.com/blog/${post.slug}`} />
        <meta name="author" content={post.author} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.eneryazilim.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:image" content={post.image_url || "https://www.eneryazilim.com/logo.png"} />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:author" content={post.author} />
        {post.tags && post.tags.map((tag, i) => (
          <meta key={i} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://www.eneryazilim.com/blog/${post.slug}`} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || post.title} />
        <meta name="twitter:image" content={post.image_url || "https://www.eneryazilim.com/logo.png"} />
        
        {/* Structured Data - BlogPosting */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt || post.title,
            "image": post.image_url || "https://www.eneryazilim.com/logo.png",
            "datePublished": post.created_at,
            "dateModified": post.updated_at || post.created_at,
            "author": {
              "@type": "Person",
              "name": post.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "Ener Yazılım",
              "url": "https://www.eneryazilim.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.eneryazilim.com/logo.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://www.eneryazilim.com/blog/${post.slug}`
            },
            "keywords": post.tags ? post.tags.join(', ') : ''
          })}
        </script>
      </Helmet>
      <Navbar />

      <article className="pt-32 pb-20" data-testid="blog-detail">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/blog"
              data-testid="back-to-blog-link"
              className="inline-flex items-center space-x-2 text-white/60 hover:text-[#FF6B35] transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Blog'a Dön</span>
            </Link>

            {/* Post Header */}
            <div className="mb-8">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="post-title">{post.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-white/60">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(post.created_at)}</span>
                </div>
              </div>
            </div>

            {/* Featured Image */}
            {post.image_url && (
              <OptimizedImage
                src={post.image_url}
                alt={post.title}
                className="mb-8 rounded-2xl overflow-hidden h-96"
                loading="eager"
                objectFit="cover"
              />
            )}

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {post.tags.map((tag, i) => (
                  <span key={i} className="flex items-center space-x-1 text-xs px-3 py-1 glass rounded-full">
                    <Tag className="w-3 h-3" />
                    <span>{tag}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Content */}
            <div
              data-testid="post-content"
              className="prose prose-invert prose-lg max-w-none"
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: '1.8',
              }}
            >
              <div className="whitespace-pre-wrap">{post.content}</div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;